import type { GameResult } from '@league/shared/contract';
import { GAMES_MAX, gameEntryPoints, normalizeScoreCell, sessionTotal, WALL_GAME_POINTS } from '@league/shared/scoring';

import type { CardReading } from './ReadCard';

/*
  THE SCORE GRID'S RULES, shared by the director's sheet and the player's card — owner, 2026-10-04:
  players get "the same score card functionality as the directors". One copy, so the two screens can
  never disagree about what a wall game is or who won.
*/

/**
 * One game, as a row of the grid: what each player scored, as typed.
 *
 * ⚠ THIS WAS `{ winner, points, wall }` UNTIL 2026-09-19, and the shape was the whole problem. It
 * could hold one number per game, so the screen could only ever ask "who won?" — while the card
 * being copied from has a cell for every player in every game. The owner's words: "I can't see the
 * score for each person, for each game."
 *
 * A missing key is an EMPTY CELL, which is not the same as a zero: a game nobody has written
 * anything in has not been played, and `toEntries` drops it rather than saving four zeroes.
 */
export interface Game {
  saved?: { points: Record<string, string>; won: Record<string, boolean>; wall?: boolean } | undefined;
  gameNumber: number;
  /** Player id → what is in that cell, exactly as typed. */
  points: Record<string, string>;
}

/** Anybody at the table, as far as the grid's rules care. */
interface Seated {
  playerId: string;
}

/** What is in one cell as a number. An empty cell reads as zero once the game has been played. */
export function cellValue(game: Game, playerId: string): number {
  /* A letter typed by mistake counts as nothing until it is fixed — the total read "NaN" (2026-10-04). */
  const value = Number(game.points[playerId] || 0);
  return Number.isFinite(value) ? value : 0;
}

/** Has anybody written anything in this row? An untouched game was not played. */
export function hasAnyEntry(game: Game): boolean {
  return Object.values(game.points).some((raw) => raw.trim() !== '');
}

function unchangedSavedGame(game: Game, players: Seated[]): boolean {
  return Boolean(game.saved && Object.keys(game.saved.points).length === players.length && players.every(p => {
    const value = game.points[p.playerId]?.trim();
    return value !== undefined && Number(value) === Number(game.saved!.points[p.playerId]);
  }));
}

/**
 * Preserve explicit saved outcomes before considering the entered score pattern.
 * Without recorded outcome evidence, only an explicitly entered 10 for every player
 * identifies a wall; a ten-point winning hand or a partly entered game does not.
 */
export function isWallGame(game: Game, players: Seated[]): boolean {
  if (unchangedSavedGame(game, players)) {
    if (Object.values(game.saved!.won).some(Boolean)) return false;
    if (game.saved!.wall !== undefined) return game.saved!.wall;
  }
  return players.length > 0 && players.every(player =>
    (game.points[player.playerId] ?? '').trim() !== '' && cellValue(game, player.playerId) === WALL_GAME_POINTS);

}

/**
 * Who scored in this game. One name is the rule; two is the mistake the grid has to catch.
 *
 * A full grid lets her type anywhere, which is what makes it match the paper card — so the rule is
 * enforced by TELLING HER, not by refusing the keystroke. Silently clearing the other cell is what
 * the old screen did, and it moved a score onto the wrong person without anybody noticing.
 */
export function scorersIn<P extends Seated>(game: Game, players: P[]): P[] {
  return players.filter((player) => cellValue(game, player.playerId) > 0);
}

/** Games that break the one-scorer rule, with who scored in them. */
export function gridProblems<P extends Seated>(games: Game[], players: P[]): { game: Game; scorers: P[] }[] {
  return games
    .filter(hasAnyEntry)
    .filter((game) => !isWallGame(game, players))
    .map((game) => ({ game, scorers: scorersIn(game, players) }))
    .filter(({ scorers }) => scorers.length > 1);
}

/** Running totals, worked out here exactly as the server will work them out. */
export function gridTotals(
  games: Game[],
  players: Seated[],
  mode: 'social' | 'tournament' | undefined,
): Map<string, { points: number; counted: number[] }> {
  const out = new Map<string, { points: number; counted: number[] }>();
  for (const player of players) {
    const scored = games
      .filter(hasAnyEntry)
      .map((game) => ({ gameNumber: game.gameNumber, points: cellValue(game, player.playerId) }));
    const total = sessionTotal(scored.map(g => ({ ...g, won: false })), mode);
    out.set(player.playerId, { points: total.points, counted: total.countedGames });
  }
  return out;
}

/**
 * What is already saved, as rows — every number from 1 to the last game, so a game missing from the
 * middle shows as an empty row she can see and fix, rather than the grid jumping from 1 to 5
 * (owner, 2026-10-14: a table on staging read "1, 5, 6, 7, 8").
 */
export function gamesFromSaved(players: (Seated & { games: GameResult[] })[]): Game[] {
  const last = Math.max(0, ...players.flatMap((p) => p.games.map((g) => g.gameNumber)));

  return Array.from({ length: last }, (_, index) => index + 1).map((gameNumber) => {
    const points: Record<string, string> = {};
    for (const player of players) {
      const saved = player.games.find((g) => g.gameNumber === gameNumber);
      if (saved) points[player.playerId] = String(gameEntryPoints(saved));
    }
    if (!players.some((p) => p.games.some((g) => g.gameNumber === gameNumber))) return { gameNumber, points };
    return { gameNumber, points, saved: { ...(players.some(p => p.games.find(g => g.gameNumber === gameNumber)?.evidence) ? {wall: players.every(p => p.games.find(g => g.gameNumber === gameNumber)?.evidence?.result.outcome === 'wall')} : {}), points: { ...points }, won: Object.fromEntries(players.map(p => [p.playerId, p.games.find(g => g.gameNumber === gameNumber)?.won ?? false])) } };
  });
}

/**
 * EMPTY GAMES THAT CANNOT SIMPLY BE LEFT OFF: one before a game that has scores (saving it would
 * leave a gap — game 1, then game 5), or one that was saved and has been cleared. Save waits until
 * each is filled in or removed. An untouched row at the END is a game not played yet, and is dropped.
 */
export function emptyGames(games: Game[]): number[] {
  const lastPlayed = Math.max(0, ...games.filter(hasAnyEntry).map((g) => g.gameNumber));
  return games
    .filter((g) => !hasAnyEntry(g) && (g.gameNumber < lastPlayed || g.saved))
    .map((g) => g.gameNumber)
    .sort((a, b) => a - b);
}

/** The director's sheet's wording for a box that is not a whole number (Courtney's). */
const directorIncomplete = () => "Finish this game’s entries or clear all entries in this new game. Use 0 for a zero score.";

/**
 * Rows the server can save. Who won is worked out from the numbers, never typed.
 *
 * `incomplete` words the refusal for a box that is not a whole number. Each screen keeps its own
 * wording (both Courtney's): the director's sheet the general line, the player's card the one that
 * names who and which game.
 */
export function toEntries<P extends Seated & { games: GameResult[] }>(
  games: Game[],
  players: P[],
  incomplete: (player: P, gameNumber: number) => string = directorIncomplete,
) {
  const entries: { playerId: string; gameNumber: number; points: number; won: boolean; incomplete?: boolean }[] = [];

  /* The grid names these and holds Save; this is the backstop, so no screen can send a gap. */
  const empty = emptyGames(games);
  if (empty.length > 0) throw new Error(emptyGamesMessage(empty));
  const played = games.filter(hasAnyEntry).map((g) => g.gameNumber).sort((a, b) => a - b);
  if (played.some((n, index) => n !== index + 1)) throw new Error('Games must be numbered in order from game 1.');

  for (const game of [...games].sort((a, b) => a.gameNumber - b.gameNumber)) {
    /** A row nobody has written in is a game that was not played, not four zeroes. */
    if (!hasAnyEntry(game)) continue;

    const wall = isWallGame(game, players);
    if (!unchangedSavedGame(game, players) && !wall && scorersIn(game, players).length !== 1) throw new Error(`Game ${game.gameNumber} needs a valid winning score or wall outcome.`);

    for (const player of players) {
      const raw = (game.points[player.playerId] ?? "").trim();
      if (raw !== '' && (!/^-?\d+$/.test(raw) || !Number.isSafeInteger(Number(raw)))) throw new Error(incomplete(player, game.gameNumber));
      const points = normalizeScoreCell(raw);
      entries.push({
        playerId: player.playerId,
        gameNumber: game.gameNumber,
        points,
        /** Nobody wins a wall game — everyone records ten and no hand was made. */
        won: unchangedSavedGame(game, players) ? game.saved!.won[player.playerId] ?? false : !wall && points > 0,
      });
    }
  }

  return entries;
}

/**
 * WHAT WOULD BE SAVED, AS ONE COMPARABLE STRING — or null when these games could not be saved as they
 * stand. Never throws: it runs while the screen is DRAWING, and for one render after she moves table
 * the games are still the last table's, read against this table's players. A throw there blanked the
 * whole score sheet on staging (owner, 2026-10-09: "failing when we navigate between tables").
 */
export function entriesSignature<P extends Seated & { games: GameResult[] }>(games: Game[], players: P[]): string | null {
  try {
    return JSON.stringify(toEntries(games, players));
  } catch {
    return null;
  }
}

/**
 * A DRAFT SAVED IN THIS BROWSER, used only if it still has the shape the grid draws: a list of games,
 * each with a whole game number and a box per player holding text. Anything else (an older version's
 * format, a write cut short) is not a draft at all, and the saved scores are shown. A malformed one used
 * to be able to blank the page, and stayed in the browser to do it again on every reload (2026-10-09).
 */
export function gamesFromStorage(value: unknown): Game[] | null {
  if (!Array.isArray(value)) return null;
  const fits = value.every((game) => {
    if (!game || typeof game !== 'object') return false;
    const { gameNumber, points } = game as { gameNumber?: unknown; points?: unknown };
    return Number.isInteger(gameNumber)
      && !!points && typeof points === 'object' && !Array.isArray(points)
      && Object.values(points as Record<string, unknown>).every((cell) => typeof cell === 'string');
  });
  return fits ? (value as Game[]) : null;
}

/** A photo's reading as rows: at least the games a table must play, and every game the card has. */
export function gamesFromReading(read: CardReading, minimum: number): Game[] {
  const last = Math.max(minimum, ...read.games.map((g) => g.gameNumber));
  return Array.from({ length: last }, (_, index) => {
    const found = read.games.find((g) => g.gameNumber === index + 1);
    return {
      gameNumber: index + 1,
      points: Object.fromEntries(Object.entries(found?.points ?? {}).map(([id, value]) => [id, String(value)])),
    };
  });
}

/*
  ⚠ A GAME THE CARD HAS WRITING IN MUST NOT BE SAVED EMPTY. An empty row is how the grid says
  "not played", and Save drops it without a word — which is right for a row she never touched and
  was exactly wrong when a reading left a scribbled game-1 row blank: Angela's 35 vanished
  (owner, 2026-10-03). So a game the reading mentioned holds Save until it is filled in.
*/
export function readButEmpty(reading: CardReading | null, games: Game[]): number[] {
  if (!reading) return [];
  return [...new Set([...reading.games.map((g) => g.gameNumber), ...reading.uncertain.map((u) => u.gameNumber)])]
    .filter((n): n is number => typeof n === 'number')
    .filter((n) => {
      const row = games.find((g) => g.gameNumber === n);
      return !row || !hasAnyEntry(row);
    })
    .sort((a, b) => a - b);
}

/** "Game 2 is empty. Fill it in or remove it." — said in the grid, and the reason Save waits. */
export function emptyGamesMessage(numbers: number[]): string {
  if (numbers.length === 1) return `Game ${numbers[0]} is empty. Fill it in or remove it.`;
  return `Games ${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]} are empty. Fill them in or remove them.`;
}

/**
 * ADD A GAME, up to {@link GAMES_MAX} — her choice, every time (owner, 2026-10-14). It used to refuse
 * while an added row was still empty, and the button looked exactly the same either way: "+ Game 6
 * is displayed, but it doesn't do anything."
 */
export function addGame(games: Game[]): Game[] {
  const last = Math.max(0, ...games.map(game => game.gameNumber));
  if (last >= GAMES_MAX) return games;
  return [...games, { gameNumber: last + 1, points: {} }];
}

/**
 * REMOVE A GAME, and the games after it move up one, so the card never has a hole in it. Removing a
 * saved game takes effect when she saves, like any other edit.
 */
export function removeGame(games: Game[], gameNumber: number): Game[] {
  return games
    .filter((game) => game.gameNumber !== gameNumber)
    .map((game) => (game.gameNumber > gameNumber ? { ...game, gameNumber: game.gameNumber - 1 } : game));
}

/**
 * IS THIS EXACTLY WHAT THE SERVER ALREADY HAS? Asked when a save fails on a weak connection: the
 * request may have arrived and only the answer been lost, in which case her scores ARE saved and a
 * second try would be refused as out of date (owner, 2026-10-14).
 */
export function matchesSaved<P extends Seated & { games: GameResult[] }>(games: Game[], players: P[]): boolean {
  let entries;
  try {
    entries = toEntries(games, players);
  } catch {
    return false;
  }
  const saved = players.flatMap((p) => p.games.map((g) => `${p.playerId}:${g.gameNumber}:${gameEntryPoints(g)}`)).sort();
  const sent = entries.map((e) => `${e.playerId}:${e.gameNumber}:${e.points}`).sort();
  return saved.length > 0 && JSON.stringify(saved) === JSON.stringify(sent);
}
